import React, { useState, useEffect, useCallback } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { 
  getDashboardLayout, 
  saveDashboardLayout, 
  removeDashboardWidget,
  getSavedReports,
  runSavedReport,
  createSavedReport,
  deleteSavedReport,
  pinReportToDashboard,
  getConnections,
  estimateAIQuery
} from '../services/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import DataVisualization from '../components/DataVisualization';
import { 
  LayoutDashboard, 
  Plus, 
  Save, 
  Trash2, 
  Play,
  GripVertical,
  BarChart3,
  LineChart,
  PieChart,
  TrendingUp,
  FileText,
  Pin,
  RefreshCw,
  X,
  Edit2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Database,
  Loader2,
  ChevronRight,
  HelpCircle
} from 'lucide-react';

const CustomDashboardPage = () => {
  const [widgets, setWidgets] = useState([]);
  const [savedReports, setSavedReports] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreateReport, setShowCreateReport] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [runningWidget, setRunningWidget] = useState(null);
  const [widgetResults, setWidgetResults] = useState({});
  
  // Create report form - enhanced
  const [newReport, setNewReport] = useState({
    name: '',
    query: '',
    description: '',
    connectionId: 'all',
    visualizationType: 'bar'
  });
  
  // Report creation flow states
  const [createStep, setCreateStep] = useState(1); // 1: Details, 2: Test, 3: Confirm
  const [testingQuery, setTestingQuery] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [creatingReport, setCreatingReport] = useState(false);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [layoutData, reportsData, connectionsData] = await Promise.all([
        getDashboardLayout(),
        getSavedReports(),
        getConnections()
      ]);
      setWidgets(layoutData.widgets || []);
      setSavedReports(reportsData.reports || []);
      setConnections(connectionsData || []);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLayoutChange = (newLayout) => {
    if (!editMode) return;
    
    const updatedWidgets = widgets.map(widget => {
      const layoutItem = newLayout.find(l => l.i === widget.id);
      if (layoutItem) {
        return {
          ...widget,
          position: { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h }
        };
      }
      return widget;
    });
    setWidgets(updatedWidgets);
  };

  const handleSaveLayout = async () => {
    setSaving(true);
    try {
      await saveDashboardLayout(widgets);
      toast.success('Dashboard layout saved');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveWidget = async (widgetId) => {
    try {
      await removeDashboardWidget(widgetId);
      setWidgets(prev => prev.filter(w => w.id !== widgetId));
      toast.success('Widget removed');
    } catch (error) {
      toast.error('Failed to remove widget');
    }
  };

  const handleRunWidget = async (widget) => {
    if (widget.type !== 'saved_query' || !widget.config?.report_id) return;
    
    setRunningWidget(widget.id);
    try {
      const result = await runSavedReport(widget.config.report_id);
      setWidgetResults(prev => ({
        ...prev,
        [widget.id]: result
      }));
      if (!result.success) {
        toast.error(result.error || 'Query failed');
      }
    } catch (error) {
      toast.error('Failed to run report');
    } finally {
      setRunningWidget(null);
    }
  };

  // Reset create report form
  const resetCreateForm = () => {
    setNewReport({
      name: '',
      query: '',
      description: '',
      connectionId: 'all',
      visualizationType: 'bar'
    });
    setCreateStep(1);
    setTestResult(null);
    setTestError(null);
  };

  const handleCloseCreateDialog = () => {
    setShowCreateReport(false);
    resetCreateForm();
  };

  // Step 1: Validate details
  const handleNextStep = () => {
    if (!newReport.name.trim()) {
      toast.error('Please enter a report name');
      return;
    }
    if (!newReport.query.trim()) {
      toast.error('Please enter a query');
      return;
    }
    if (newReport.query.trim().length < 10) {
      toast.error('Query is too short. Please be more specific.');
      return;
    }
    setCreateStep(2);
    handleTestQuery();
  };

  // Step 2: Test the query
  const handleTestQuery = async () => {
    setTestingQuery(true);
    setTestResult(null);
    setTestError(null);
    
    try {
      const connectionId = newReport.connectionId === 'all' ? null : newReport.connectionId;
      const estimate = await estimateAIQuery(newReport.query, connectionId);
      
      if (estimate.error) {
        setTestError(estimate.error);
      } else {
        setTestResult(estimate);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to test query';
      setTestError(errorMsg);
    } finally {
      setTestingQuery(false);
    }
  };

  // Step 3: Create the report
  const handleCreateReport = async () => {
    setCreatingReport(true);
    
    try {
      const connectionId = newReport.connectionId === 'all' ? null : newReport.connectionId;
      await createSavedReport(
        newReport.name,
        newReport.query,
        connectionId,
        null,
        newReport.visualizationType
      );
      toast.success('Report created successfully!');
      handleCloseCreateDialog();
      fetchDashboard();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create report';
      toast.error(errorMsg);
    } finally {
      setCreatingReport(false);
    }
  };

  const handlePinReport = async (reportId) => {
    try {
      await pinReportToDashboard(reportId);
      toast.success('Report pinned to dashboard');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to pin report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report? It will also be removed from your dashboard.')) return;
    try {
      await deleteSavedReport(reportId);
      setSavedReports(prev => prev.filter(r => r.id !== reportId));
      // Also remove from widgets
      setWidgets(prev => prev.filter(w => w.config?.report_id !== reportId));
      toast.success('Report deleted');
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const renderWidget = (widget) => {
    const result = widgetResults[widget.id];
    const isRunning = runningWidget === widget.id;
    
    switch (widget.type) {
      case 'saved_query':
        return (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between p-2 border-b border-gray-100">
              <span className="font-medium text-sm text-gray-700 truncate">{widget.title}</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRunWidget(widget)}
                  disabled={isRunning}
                  className="h-7 w-7 p-0"
                >
                  {isRunning ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <Play className="w-3 h-3" />
                  )}
                </Button>
                {editMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveWidget(widget.id)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex-1 p-2 overflow-auto">
              {result ? (
                result.visualization ? (
                  <DataVisualization visualization={result.visualization} />
                ) : result.success ? (
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-1">Result:</p>
                    <p>{result.analysis?.substring(0, 200)}...</p>
                  </div>
                ) : (
                  <div className="text-sm text-red-600 p-2 bg-red-50 rounded">
                    <p className="font-medium">Error:</p>
                    <p>{result.error || 'Query failed'}</p>
                  </div>
                )
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  Click play to run
                </div>
              )}
            </div>
          </div>
        );
      
      case 'metric':
        return (
          <div className="h-full flex flex-col items-center justify-center p-4">
            <div className="text-3xl font-bold text-gray-900">{widget.config?.value || '—'}</div>
            <div className="text-sm text-gray-500 mt-1">{widget.title}</div>
            {editMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveWidget(widget.id)}
                className="absolute top-1 right-1 h-6 w-6 p-0 text-red-500"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        );
      
      default:
        return (
          <div className="h-full flex items-center justify-center text-gray-400">
            Unknown widget type
          </div>
        );
    }
  };

  const layout = widgets.map(widget => ({
    i: widget.id,
    x: widget.position?.x || 0,
    y: widget.position?.y || 0,
    w: widget.position?.w || 4,
    h: widget.position?.h || 3,
    minW: 2,
    minH: 2
  }));

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'simple': return 'bg-green-100 text-green-700 border-green-200';
      case 'moderate': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'complex': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="p-8" data-testid="custom-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            My Dashboard
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Customize your dashboard with saved reports and widgets
          </p>
        </div>
        <div className="flex items-center gap-3">
          {editMode ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => setEditMode(false)}
                className="border-gray-200"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveLayout}
                disabled={saving}
                className="btn-gold"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Layout'}
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => setShowCreateReport(true)}
                className="border-gray-200"
              >
                <FileText className="w-4 h-4 mr-2" />
                New Report
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setEditMode(true)}
                className="border-gray-200"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit Layout
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Main Dashboard Area */}
        <div className="col-span-12 lg:col-span-9">
          <Card className="bg-white border-gray-200 min-h-[600px] p-4">
            {loading ? (
              <div className="grid grid-cols-3 gap-4">
                <Skeleton className="h-40" />
                <Skeleton className="h-40" />
                <Skeleton className="h-40" />
              </div>
            ) : widgets.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-500 py-20">
                <LayoutDashboard className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-lg font-medium">Your dashboard is empty</p>
                <p className="text-sm mt-1">Create a report and pin it to get started</p>
                <Button 
                  onClick={() => setShowCreateReport(true)}
                  className="btn-gold mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Report
                </Button>
              </div>
            ) : (
              <GridLayout
                className="layout"
                layout={layout}
                cols={12}
                rowHeight={80}
                width={900}
                isDraggable={editMode}
                isResizable={editMode}
                onLayoutChange={handleLayoutChange}
              >
                {widgets.map(widget => (
                  <div 
                    key={widget.id}
                    className={`bg-gray-50 rounded-lg border ${editMode ? 'border-dashed border-[#D4AF37]' : 'border-gray-200'} overflow-hidden relative`}
                  >
                    {editMode && (
                      <div className="absolute top-1 left-1 z-10 cursor-move">
                        <GripVertical className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    {renderWidget(widget)}
                  </div>
                ))}
              </GridLayout>
            )}
          </Card>
        </div>

        {/* Saved Reports Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <Card className="bg-white border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
                Saved Reports
              </h2>
            </div>
            <ScrollArea className="h-[500px]">
              {savedReports.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No saved reports yet
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {savedReports.map(report => (
                    <div key={report.id} className="p-3 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-gray-900 truncate">{report.name}</p>
                          <p className="text-xs text-gray-500 truncate mt-1">{report.query}</p>
                          {report.run_count > 0 && (
                            <p className="text-xs text-gray-400 mt-1">
                              Run {report.run_count}x • Last: {new Date(report.last_run).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePinReport(report.id)}
                            className="h-7 w-7 p-0 text-[#D4AF37]"
                            title="Pin to dashboard"
                          >
                            <Pin className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteReport(report.id)}
                            className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </Card>
        </div>
      </div>

      {/* Create Report Dialog - Enhanced Multi-Step */}
      <Dialog open={showCreateReport} onOpenChange={handleCloseCreateDialog}>
        <DialogContent className="bg-white border-gray-200 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Create Saved Report
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              {createStep === 1 && "Step 1: Enter report details"}
              {createStep === 2 && "Step 2: Testing your query..."}
              {createStep === 3 && "Step 3: Confirm and create"}
            </DialogDescription>
          </DialogHeader>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 py-2">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  createStep >= step 
                    ? 'bg-[#D4AF37] text-white' 
                    : 'bg-gray-100 text-gray-400'
                }`}>
                  {createStep > step ? <CheckCircle2 className="w-4 h-4" /> : step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 rounded ${
                    createStep > step ? 'bg-[#D4AF37]' : 'bg-gray-100'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          
          {/* Step 1: Details */}
          {createStep === 1 && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Report Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newReport.name}
                  onChange={(e) => setNewReport(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Daily Login Count, Revenue Summary"
                  className="bg-white border-gray-200"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Query <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={newReport.query}
                  onChange={(e) => setNewReport(prev => ({ ...prev, query: e.target.value }))}
                  placeholder="Ask a question in natural language, e.g., 'How many logins today for brandId 31?' or 'Show top 10 users by wager amount'"
                  className="bg-white border-gray-200 min-h-[100px]"
                />
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <HelpCircle className="w-3 h-3" />
                  Tip: Be specific. Include filters like brandId, date range, etc.
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Database Server
                </label>
                <Select 
                  value={newReport.connectionId} 
                  onValueChange={(v) => setNewReport(prev => ({ ...prev, connectionId: v }))}
                >
                  <SelectTrigger className="w-full bg-white border-gray-200">
                    <SelectValue placeholder="Select server..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="all" className="text-gray-700">
                      All Servers (Auto-detect)
                    </SelectItem>
                    {connections.map(conn => (
                      <SelectItem key={conn.id} value={conn.id} className="text-gray-700">
                        {conn.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Visualization Type
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { value: 'bar', icon: BarChart3, label: 'Bar' },
                    { value: 'line', icon: LineChart, label: 'Line' },
                    { value: 'pie', icon: PieChart, label: 'Pie' },
                    { value: 'area', icon: TrendingUp, label: 'Area' }
                  ].map(viz => (
                    <button
                      key={viz.value}
                      onClick={() => setNewReport(prev => ({ ...prev, visualizationType: viz.value }))}
                      className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                        newReport.visualizationType === viz.value 
                          ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <viz.icon className="w-5 h-5 text-gray-600" />
                      <span className="text-xs text-gray-600">{viz.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Testing */}
          {createStep === 2 && (
            <div className="space-y-4 py-4">
              {testingQuery ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mb-4" />
                  <p className="text-gray-600 font-medium">Testing your query...</p>
                  <p className="text-gray-400 text-sm mt-1">Analyzing complexity and validating against schema</p>
                </div>
              ) : testError ? (
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Query Test Failed</p>
                        <p className="text-sm text-red-600 mt-1">{testError}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="font-medium text-amber-800 mb-2">How to fix:</p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      <li>• Check if your query is clear and specific</li>
                      <li>• Make sure you're asking about data that exists in the database</li>
                      <li>• Try selecting a specific database server instead of "All"</li>
                      <li>• Use the AI Query page to test and refine your question first</li>
                    </ul>
                  </div>
                </div>
              ) : testResult ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-800">Query Test Passed</p>
                        <p className="text-sm text-green-600 mt-1">Your query looks valid and can be executed.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-xs text-gray-500">Estimated Time</span>
                      </div>
                      <p className="font-medium text-gray-900">{testResult.estimated_time_display || 'Unknown'}</p>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <BarChart3 className="w-4 h-4 text-gray-400" />
                        <span className="text-xs text-gray-500">Complexity</span>
                      </div>
                      <Badge className={`${getComplexityColor(testResult.complexity)} border`}>
                        {testResult.complexity?.toUpperCase() || 'UNKNOWN'}
                      </Badge>
                    </div>
                  </div>
                  
                  {testResult.data_description && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs text-blue-600 font-medium mb-1">What this query will do:</p>
                      <p className="text-sm text-blue-800">{testResult.data_description}</p>
                    </div>
                  )}
                  
                  {testResult.servers_involved && testResult.servers_involved.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Database className="w-4 h-4" />
                      <span>Servers: {testResult.servers_involved.join(', ')}</span>
                    </div>
                  )}
                  
                  {testResult.warning && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                      <p className="text-sm text-amber-800">{testResult.warning}</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
          
          {/* Step 3: Confirm */}
          {createStep === 3 && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                <div>
                  <span className="text-xs text-gray-500">Report Name</span>
                  <p className="font-medium text-gray-900">{newReport.name}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Query</span>
                  <p className="text-sm text-gray-700">{newReport.query}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-xs text-gray-500">Visualization</span>
                    <p className="text-sm text-gray-700 capitalize">{newReport.visualizationType} Chart</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Server</span>
                    <p className="text-sm text-gray-700">
                      {newReport.connectionId === 'all' 
                        ? 'Auto-detect' 
                        : connections.find(c => c.id === newReport.connectionId)?.name || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>Note:</strong> After creating, you can pin this report to your dashboard and run it anytime to get fresh data.
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {createStep === 1 && (
              <>
                <Button variant="outline" onClick={handleCloseCreateDialog}>
                  Cancel
                </Button>
                <Button onClick={handleNextStep} className="btn-gold">
                  Test Query
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            )}
            
            {createStep === 2 && (
              <>
                <Button variant="outline" onClick={() => setCreateStep(1)}>
                  Back
                </Button>
                {testError ? (
                  <Button onClick={handleTestQuery} className="btn-gold">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Retry Test
                  </Button>
                ) : testResult ? (
                  <Button onClick={() => setCreateStep(3)} className="btn-gold">
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : null}
              </>
            )}
            
            {createStep === 3 && (
              <>
                <Button variant="outline" onClick={() => setCreateStep(2)}>
                  Back
                </Button>
                <Button onClick={handleCreateReport} disabled={creatingReport} className="btn-gold">
                  {creatingReport ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Report
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomDashboardPage;
